import type { ErrorEvent } from "@sentry/react";

export function sentryBeforeSend(event: ErrorEvent): ErrorEvent | null {
  const exceptions = event.exception?.values;
  if (exceptions?.length !== 1) return event;
  const exception = exceptions[0];
  const frames = exception.stacktrace?.frames;

  if (
    exception.type !== "RangeError" ||
    exception.value !== "Maximum call stack size exceeded." ||
    exception.mechanism?.type !== "onerror" ||
    exception.mechanism.handled !== false ||
    !event.request?.url ||
    !frames ||
    frames.length < 12
  ) {
    return event;
  }

  // Match the known signature on any device, independently of User-Agent.
  // Only the observed el_main build is suppressed. A rebuilt Translate script
  // must be investigated again rather than hiding unrelated stack overflows.
  // https://issuetracker.google.com/issues/546919943
  try {
    const documentUrl = new URL(event.request.url);
    if (!/^https?:$/.test(documentUrl.protocol)) return event;
    for (const frame of frames) {
      if (!frame.filename) return event;
      // Injected frames may retain previous SPA route URLs. Do not accept
      // bundles, source files, extension scripts, or missing source locations.
      const source = new URL(frame.filename, documentUrl);
      if (
        source.origin !== documentUrl.origin ||
        /\.[^/]+$/.test(source.pathname)
      ) {
        return event;
      }
    }
  } catch {
    return event;
  }

  // WebKit and Chrome can expose this recursion in opposite stack orders.
  // Normalize a copy so the original event remains untouched if it is kept.
  const orderedFrames =
    frames[0].function === "Fk" ? frames : [...frames].reverse();
  let recursionLength = 0;
  for (const frame of orderedFrames) {
    const expectedFunction = recursionLength % 2 === 0 ? "Fk" : "Hk";
    const expectedColumn = recursionLength % 2 === 0 ? 63 : 409;
    if (
      frame.function !== expectedFunction ||
      frame.lineno !== 230 ||
      frame.colno !== expectedColumn
    ) {
      break;
    }
    recursionLength++;
  }
  if (recursionLength < 12) return event;

  const knownEntryFrames = orderedFrames
    .slice(recursionLength)
    .every(
      (frame) =>
        (frame.function === "Hk" &&
          frame.lineno === 230 &&
          frame.colno === 383) ||
        ((!frame.function || frame.function === "?") &&
          ((frame.lineno === 194 && frame.colno === 41) ||
            (frame.lineno === 202 && frame.colno === 201) ||
            (frame.lineno === 201 && frame.colno === 363)))
    );
  if (knownEntryFrames) return null;
  return event;
}
